import { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Card,
    FloatingButton,
    IconCloseSkinny,
    IconPlayFilled,
    MediaControl,
    MediaItem,
    Spinner,
    type TestProps,
    Text,
    VStack,
} from '@direct-frontend/components';
import {
    MEDIA_TYPE_VIDEO,
    type MediaV2Field,
    VIDEO_STATUS_READY,
    VIDEO_STATUSES_SHOW_ERROR,
    VIDEO_STATUSES_SHOW_LOADER,
    type VideoField,
} from '../../lib/form';
import { AdaptiveMedia } from '../AdaptiveImage';
import styles from './MediaGalleryItem.module.css';

type MediaGalleryItemProps = {
    media: MediaV2Field;
    onDeleteImage?: VoidFunction;
    onClick?: VoidFunction;
    width?: number;
    height?: number;
    showPlay?: boolean;
} & TestProps;

const IMAGE_BORDER_RADIUS = '14';

export const MediaGalleryItem = memo(function MediaGalleryItem({
    onDeleteImage,
    media,
    onClick,
    width = 120,
    height = 120,
    showPlay = true,
    'data-testid': dataTestId = 'MediaGalleryItem',
}: MediaGalleryItemProps) {
    if (media.type === MEDIA_TYPE_VIDEO) {
        return (
            <MediaGalleryVideoItem
                media={media}
                onClick={onClick}
                width={width}
                height={height}
                onDeleteImage={onDeleteImage}
                showPlay={showPlay}
                data-testid={dataTestId}
            />
        );
    }

    return (
        <MediaItem
            borderRadius={IMAGE_BORDER_RADIUS}
            onClick={onClick}
            controls={
                <>
                    {onDeleteImage && (
                        <MediaControl position="top-right">
                            <FloatingButton
                                size="2xs"
                                color="contrast"
                                icon={IconCloseSkinny}
                                onClick={onDeleteImage}
                            />
                        </MediaControl>
                    )}
                </>
            }
            media={<MediaGalleryImage width={width} height={height} media={media} loading="lazy" />}
            data-testid={dataTestId}
        />
    );
});

export const MediaGalleryVideoItem = memo(function MediaGalleryItem({
    onDeleteImage,
    media,
    onClick,
    width = 120,
    height = 120,
    showPlay = true,
    'data-testid': dataTestId = 'MediaGalleryVideoItem',
}: {
    media: VideoField;
    onDeleteImage?: VoidFunction;
    onClick?: VoidFunction;
    width?: number;
    height?: number;
    showPlay?: boolean;
} & TestProps) {
    const isReady = media.status === VIDEO_STATUS_READY;

    const mediaComponent = isReady ? (
        <MediaGalleryImage width={width} height={height} media={media} loading="lazy" data-testid={dataTestId} />
    ) : (
        <div style={{ width, height }} data-testid={dataTestId}>
            <Card padding="12" color="gray" width="max" height="max">
                <VStack justifyContent="center" gap="6" alignItems="center" height="max">
                    {VIDEO_STATUSES_SHOW_LOADER.includes(media.status) && (
                        <>
                            <Spinner color="black" size="24" />
                            {width < 200 && height < 200 ? (
                                <Text textAlign="center" color="gray" role="supplementary" size="xs">
                                    Видео обрабатывается
                                </Text>
                            ) : (
                                <>
                                    <Text color="gray" role="supplementary">
                                        Видео обрабатывается...
                                    </Text>
                                    <Text color="gray" role="supplementary" size="xs">
                                        Это может занять несколько минут
                                    </Text>
                                </>
                            )}
                        </>
                    )}
                    {VIDEO_STATUSES_SHOW_ERROR.includes(media.status) && (
                        <Text color="gray" role="supplementary">
                            Ошибка при загрузке видео
                        </Text>
                    )}
                </VStack>
            </Card>
        </div>
    );

    return (
        <MediaItem
            borderRadius={IMAGE_BORDER_RADIUS}
            onClick={onClick}
            controls={
                <>
                    {onDeleteImage && (
                        <MediaControl position="top-right">
                            <FloatingButton
                                size="2xs"
                                color="contrast"
                                icon={IconCloseSkinny}
                                onClick={onDeleteImage}
                            />
                        </MediaControl>
                    )}
                    {showPlay && isReady && (
                        <MediaControl isAlwaysVisible position="center">
                            <FloatingButton size="m" color="contrast" icon={IconPlayFilled} />
                        </MediaControl>
                    )}
                </>
            }
            media={mediaComponent}
            data-testid={dataTestId}
        />
    );
});

export const MediaGalleryImage = ({
    media,
    loading,
    onClick,
    width,
    height,
    'data-testid': dataTestId = 'MediaGalleryImage',
}: {
    media: MediaV2Field;
    loading?: HTMLImageElement['loading'];
    onClick?: VoidFunction;
    width: number;
    height: number;
} & TestProps) => (
    <AdaptiveMedia
        onClick={onClick}
        media={media}
        borderRadius={IMAGE_BORDER_RADIUS}
        className={styles.wrapper}
        alt="Превью"
        loading={loading}
        width={width}
        height={height}
        fit="contain"
        background="gray"
        data-testid={dataTestId}
    />
);

export const SortableMediaGalleryItem = (props: MediaGalleryItemProps) => {
    const {
        media: { id },
    } = props;

    const { onClick, ...other } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition ?? '',
        opacity: isDragging ? 0.5 : 1,
        height: other.height ? `${other.height}px` : undefined,
        borderRadius: `${IMAGE_BORDER_RADIUS}px`,
    };

    return (
        <div ref={setNodeRef} style={style} {...listeners} {...attributes} onClick={onClick}>
            <MediaGalleryItem {...other} />
        </div>
    );
};
